import { FC } from "react";
import { Loader } from "components/layouts";
import { gql, useQuery } from "@apollo/client";
import lodash from "lodash";
import moment from "moment";
import {classNames, useUrlState} from "utils";
import AllocateMeterDeliveryMaterialsContainer from "./cards/allocate-materials";
import {Disclosure, DisclosureButton, DisclosurePanel} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import { ArchiveBoxIcon} from "@heroicons/react/24/outline";

const GET_MATERIAL = gql`
  query GetMeterDeliveryRequestMaterial($id: ID!) {
    meterDeliveryRequestMaterials: getMeterDeliveryRequestMaterial(id: $id) {
      _id
      allocatedAt
      allocatedBy {
        firstName
        lastName
        phoneNumber
      }
      allocationNotes
      allocations {
        districtName
        districtCode
        materials {
          quantity
          material {
            _id
            name
            code
          }
        }
      }
      code
      completedAt
      completionNotes
      createdAt
      status
      submissionNotes
      submittedAt
      submittedBy {
        firstName
        lastName
        phoneNumber
      }
      completedBy {
        firstName
        lastName
        phoneNumber
      }
      materials {
        quantity
        material {
          _id
          code
          name
        }
      }
    }
  }
`;

interface MaterialsDetailsContainer {
  deliveryRequest: any;
  id: string;
  code: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
const MaterialsDetailsContainer: FC<
  MaterialsDetailsContainer
> = ({ deliveryRequest }) => {

  const [modal, setModal] = useUrlState("modal")

  const { data, loading } = useQuery(GET_MATERIAL, {
    variables: {
      id: deliveryRequest?.response?.materials?._id,
    },
    fetchPolicy: "network-only",
  });

  const materials = data?.meterDeliveryRequestMaterials

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <Loader />
        ) : (
          <div className="p-6 overflow-y-auto flex-1">
            <div className="flex justify-between items-center cursor-pointer">
              <div>
                <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                  Complementary Materials Information
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Details about the complementary materials submitted by the meter supplier
                </p>
              </div>
            </div>
            <div>
              <div className='space-y-6  divide-y divide-gray-200 overflow-y-auto flex-1'>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200 mt-4">
                  <div className='pt-4 first:pt-0'>
                    <div className='grid grid-cols-3 gap-6 mt-2'>
                      <div className="col-span-1">
                        <span className="block text-sm font-light text-gray-700">
                          Code
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {materials.code || "N/A"}
                        </div>
                      </div>
                      <div className={""}>
                        <span className="block text-sm font-light text-gray-700">
                          Status
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {lodash.startCase(materials?.status) || "N/A"}
                        </div>
                      </div>
                      <div className={""}>
                        <span className="block text-sm font-light text-gray-700">
                          Number of Materials
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {materials?.materials?.length
                            ? lodash.sumBy(materials.materials, 'quantity')
                            : "N/A"}
                        </div>
                      </div>
                      <div className={"col-start-1"}>
                        <span className="block text-sm font-light text-gray-700">
                          Submitted By
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {[
                            (
                              materials?.submittedBy?.firstName || ""
                            )?.trim(),
                            (
                              materials?.submittedBy?.lastName || ""
                            )?.trim(),
                          ]
                            .join(" ")
                            .trim() || "N A"}
                        </div>
                      </div>
                      <div className={""}>
                        <span className="block text-sm font-light text-gray-700">
                          Date Submitted
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {materials?.submittedAt
                            ? moment(materials?.submittedAt).format("DD/MM/YYYY")
                            : "N A"}
                        </div>
                      </div>
                      <div className={"col-start-1 col-span-3"}>
                        <span className="block text-sm font-light text-gray-700">
                          Submission Notes
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {materials?.submissionNotes || "N/A"}
                        </div>
                      </div>
                      {
                        Boolean(materials.allocatedAt) && (
                          <>
                            <div className={"col-start-1"}>
                              <span className="block text-sm font-light text-gray-700">
                                Allocated to districts by
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {[
                                  (
                                    materials?.allocatedBy?.firstName || ""
                                  )?.trim(),
                                  (
                                    materials?.allocatedBy?.lastName || ""
                                  )?.trim(),
                                ]
                                  .join(" ")
                                  .trim() || "N A"}
                              </div>
                            </div>
                            <div className={""}>
                              <span className="block text-sm font-light text-gray-700">
                                Date Allocated
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {materials?.allocatedAt
                                  ? moment(materials?.allocatedAt).format("DD/MM/YYYY")
                                  : "N A"}
                              </div>
                            </div>
                            <div className={"col-start-1 col-span-3"}>
                              <span className="block text-sm font-light text-gray-700">
                                Allocation Notes
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {materials?.allocationNotes || "N/A"}
                              </div>
                            </div>
                          </>
                        )
                      }
                      {
                        Boolean(materials.completedAt) && (
                          <>
                            <div className={"col-start-1"}>
                              <span className="block text-sm font-light text-gray-700">
                                Confirmed By
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {[
                                  (
                                    materials?.completedBy?.firstName || ""
                                  )?.trim(),
                                  (
                                    materials?.completedBy?.lastName || ""
                                  )?.trim(),
                                ]
                                  .join(" ")
                                  .trim() || "N A"}
                              </div>
                            </div>
                            <div className={""}>
                              <span className="block text-sm font-light text-gray-700">
                                Date Confirmed
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {materials?.completedAt
                                  ? moment(materials?.completedAt).format("DD/MM/YYYY")
                                  : "N A"}
                              </div>
                            </div>
                            <div className={"col-start-1 col-span-3"}>
                              <span className="block text-sm font-light text-gray-700">
                                Completion Notes
                              </span>
                              <div className="mt-1 block w-full sm:text-sm">
                                {materials?.completionNotes || "N/A"}
                              </div>
                            </div>
                          </>
                        )
                      }
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className='pt-4 first:pt-0'>
                    <table className="min-w-full divide-y divide-gray-200 bg-white border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="pl-3 w-[40px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            No.
                          </th>
                          <th
                            scope="col"
                            className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Material
                          </th>
                          <th
                            scope="col"
                            className="w-[100px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Quantity
                          </th>
                        </tr>
                      </thead>

                      {/* Table Body */}
                      <tbody className="divide-y divide-gray-200">
                        {
                          lodash.times(
                            materials?.materials?.length,
                            (idx) => {
                              const id = `materials.${idx}`;

                              return (
                                <tr key={idx} className="divide-x divide-gray-200">
                                  <td className="p-0">
                                    <input
                                      key={idx}
                                      type={"text"}
                                      id={`index`}
                                      name={`index`}
                                      value={`${idx + 1}`}
                                      placeholder={"eg. 1"}
                                      disabled
                                      className={classNames(
                                        "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                        "block w-[40px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                      )}
                                    />
                                  </td>
                                  <td className="p-0 flex-1">
                                    <input
                                      key={idx}
                                      value={lodash.get(materials, `${id}.material.name`, "")}
                                      disabled
                                      placeholder={"eg. "}
                                      className={classNames(
                                        "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                                        "block w-flex sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                      )}
                                    />
                                  </td>
                                  <td className="">
                                    <input
                                      key={idx}
                                      type={"number"}
                                      value={lodash.get(materials, `${id}.quantity`, "")}
                                      disabled
                                      placeholder={"eg. 1"}
                                      className={classNames(
                                        "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                                        "block w-[100px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                      )}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className={"pt-4"}>
                  <span className='text-xs font-light'>Materials Allocation Information</span>
                  <div className='pt-4'>
                    {
                      Boolean(materials?.allocations?.length) ? (
                        <div className='space-y-6 divide-y divide-gray-200'>
                          {
                            lodash.times(
                              materials?.allocations?.length,
                              (idx) => {

                                const id = `allocations.${idx}`;
                                const allocation = lodash.get(materials, id);

                                return (
                                  <div key={idx} className='border w-full border-gray-300 bg-gray-50 p-4 rounded'>
                                    <div className='space-y-4'>
                                      <div className='col-span-1'>
                                        <div>
                                          <Disclosure as="div" className='flex flex-col gap-3'>
                                            <DisclosureButton className='group flex flex-grow justify-between items-center'>
                                              <div className="flex flex-col items-start gap-2">
                                                <span className='text-xs font-light'>{allocation?.districtCode}</span>
                                                <span className='text-sm font-medium text-gray-900'>
                                                  {allocation?.districtName}
                                                </span>
                                              </div>
                                              <ChevronDownIcon
                                                className="w-5 h-5 shrink-0 text-gray-400 group-data-[open]:rotate-180"
                                              />
                                            </DisclosureButton>
                                            <DisclosurePanel
                                              transition
                                              className="grid grid-cols-4 gap-x-12 gap-y-4 transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0">

                                              <table className="min-w-full divide-y divide-gray-200 bg-white border col-span-4">
                                                <thead className="bg-gray-50">
                                                <tr>
                                                  <th
                                                    scope="col"
                                                    className="pl-3 w-[40px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                  >
                                                    No.
                                                  </th>
                                                  <th
                                                    scope="col"
                                                    className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                  >
                                                    Material
                                                  </th>
                                                  <th
                                                    scope="col"
                                                    className="w-[100px] p-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                  >
                                                    Quantity
                                                  </th>
                                                </tr>
                                                </thead>
                                                {/* Table Body */}
                                                <tbody className="divide-y divide-gray-200">
                                                {
                                                  lodash.times(
                                                    allocation.materials?.length,
                                                    (iddx) => {
                                                      const idd = `allocations.${idx}.materials.${iddx}`;
                                                      const value = lodash.get(materials, `${idd}.quantity`, "")

                                                      return (
                                                        <tr key={iddx} className="divide-x divide-gray-200">
                                                          <td className="p-0">
                                                            <input
                                                              key={iddx}
                                                              type={"text"}
                                                              id={`index`}
                                                              name={`index`}
                                                              value={`${iddx + 1}`}
                                                              placeholder={"eg. 1"}
                                                              disabled
                                                              className={classNames(
                                                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200 bg-gray-50",
                                                                "block w-[40px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                                              )}
                                                            />
                                                          </td>
                                                          <td className="p-0 flex-1">
                                                            <input
                                                              key={idx}
                                                              id={`${idd}.material`}
                                                              value={lodash.get(materials, `${idd}.material.name`)}
                                                              disabled
                                                              placeholder={"eg. 1"}
                                                              className={classNames(
                                                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                                                                "block w-full sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                                              )}
                                                            />
                                                          </td>
                                                          <td className="">
                                                            <input
                                                              key={idx}
                                                              type={"number"}
                                                              id={`${idd}.quantity`}
                                                              name={`${idd}.quantity`}
                                                              value={value}
                                                              disabled
                                                              placeholder={"eg. 1"}
                                                              className={classNames(
                                                                "focus:ring-primary-500 focus:border-primary-500 border-gray-200",
                                                                "block w-[100px] sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] border-0 focus:border"
                                                              )}
                                                            />
                                                          </td>
                                                        </tr>
                                                      );
                                                    })
                                                }
                                                </tbody>
                                              </table>
                                            </DisclosurePanel>
                                          </Disclosure>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              }
                            )
                          }
                        </div>
                      ) : (
                        <div className='flex  mt-2 w-full items-center justify-center'>
                          <div className='relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'>
                            <ArchiveBoxIcon
                              className='mx-auto h-10 w-10 text-gray-400'
                              stroke='currentColor'
                              strokeWidth={1}
                              aria-hidden='true'
                            />
                            <h3 className='mt-2 text-sm font-medium text-gray-900'>
                              No materials assigned
                            </h3>
                            <p className='mt-1 text-sm text-gray-500'>
                              Get started by adding a district below.
                            </p>
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 pb-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse sm:items-center border-t border-gray-200">
        {materials?.status === "Submitted" && (
          <button
            type="button"
            onClick={() => setModal("allocate")}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Allocate to Districts
          </button>
        )}
      </div>
      {
        Boolean(modal) && (
          <>
            <AllocateMeterDeliveryMaterialsContainer
              open={modal === 'allocate'}
              materials={materials}
              setOpen={(val) => setModal(val ? 'allocate' : undefined)}
            />
          </>
        )
      }
    </div>
  );
};

export default MaterialsDetailsContainer;
